import { convertMoneyToNumber } from './currency';
import {
  eventDateFromListingDescription,
  eventDateHasExplicitTime,
  isTicketNetworkEventListing,
  listingEventCalendarDayFromPublicData,
  normalizeListingEventDateString,
} from './listingEventDate';
import {
  buildListingSeoCopy,
  resolveEventEntityName,
} from './listingSeoCopy';
import { buildBreadcrumbListSchema } from './seoDiscoverySchema';
import { tnListingUnavailableReason } from './tnSeaticsEmptyInventory';
import { canonicalRoutePath } from './routes';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';

const SCHEMA_CONTEXT = 'https://schema.org';
const EVENT_SCHEDULED = 'https://schema.org/EventScheduled';
const OFFLINE_ATTENDANCE = 'https://schema.org/OfflineEventAttendanceMode';
const IN_STOCK = 'https://schema.org/InStock';
const OUT_OF_STOCK = 'https://schema.org/OutOfStock';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_OFFSET = /([+-]\d{2}:\d{2})$/;
const ISO_Z = /[zZ]$/;
const ISO_LOCAL_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** US state / region → IANA zone for attaching offsets when TN omits them. */
const REGION_TIMEZONES = {
  AL: 'America/Chicago',
  AK: 'America/Anchorage',
  AZ: 'America/Phoenix',
  AR: 'America/Chicago',
  CA: 'America/Los_Angeles',
  CO: 'America/Denver',
  CT: 'America/New_York',
  DC: 'America/New_York',
  DE: 'America/New_York',
  FL: 'America/New_York',
  GA: 'America/New_York',
  HI: 'Pacific/Honolulu',
  IA: 'America/Chicago',
  ID: 'America/Boise',
  IL: 'America/Chicago',
  IN: 'America/Indiana/Indianapolis',
  KS: 'America/Chicago',
  KY: 'America/New_York',
  LA: 'America/Chicago',
  MA: 'America/New_York',
  MD: 'America/New_York',
  ME: 'America/New_York',
  MI: 'America/Detroit',
  MN: 'America/Chicago',
  MO: 'America/Chicago',
  MS: 'America/Chicago',
  MT: 'America/Denver',
  NC: 'America/New_York',
  ND: 'America/Chicago',
  NE: 'America/Chicago',
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NM: 'America/Denver',
  NV: 'America/Los_Angeles',
  NY: 'America/New_York',
  OH: 'America/New_York',
  OK: 'America/Chicago',
  OR: 'America/Los_Angeles',
  PA: 'America/New_York',
  RI: 'America/New_York',
  SC: 'America/New_York',
  SD: 'America/Chicago',
  TN: 'America/Chicago',
  TX: 'America/Chicago',
  UT: 'America/Denver',
  VA: 'America/New_York',
  VT: 'America/New_York',
  WA: 'America/Los_Angeles',
  WI: 'America/Chicago',
  WV: 'America/New_York',
  WY: 'America/Denver',
};

/** Full state / district names → USPS code (TN sometimes stores "New York" not "NY"). */
const STATE_NAME_TO_CODE = {
  ALABAMA: 'AL',
  ALASKA: 'AK',
  ARIZONA: 'AZ',
  ARKANSAS: 'AR',
  CALIFORNIA: 'CA',
  COLORADO: 'CO',
  CONNECTICUT: 'CT',
  DELAWARE: 'DE',
  'DISTRICT OF COLUMBIA': 'DC',
  FLORIDA: 'FL',
  GEORGIA: 'GA',
  HAWAII: 'HI',
  IDAHO: 'ID',
  ILLINOIS: 'IL',
  INDIANA: 'IN',
  IOWA: 'IA',
  KANSAS: 'KS',
  KENTUCKY: 'KY',
  LOUISIANA: 'LA',
  MAINE: 'ME',
  MARYLAND: 'MD',
  MASSACHUSETTS: 'MA',
  MICHIGAN: 'MI',
  MINNESOTA: 'MN',
  MISSISSIPPI: 'MS',
  MISSOURI: 'MO',
  MONTANA: 'MT',
  NEBRASKA: 'NE',
  NEVADA: 'NV',
  'NEW HAMPSHIRE': 'NH',
  'NEW JERSEY': 'NJ',
  'NEW MEXICO': 'NM',
  'NEW YORK': 'NY',
  'NORTH CAROLINA': 'NC',
  'NORTH DAKOTA': 'ND',
  OHIO: 'OH',
  OKLAHOMA: 'OK',
  OREGON: 'OR',
  PENNSYLVANIA: 'PA',
  'RHODE ISLAND': 'RI',
  'SOUTH CAROLINA': 'SC',
  'SOUTH DAKOTA': 'SD',
  TENNESSEE: 'TN',
  TEXAS: 'TX',
  UTAH: 'UT',
  VERMONT: 'VT',
  VIRGINIA: 'VA',
  WASHINGTON: 'WA',
  'WEST VIRGINIA': 'WV',
  WISCONSIN: 'WI',
  WYOMING: 'WY',
};

const normalizeUsStateCode = value => {
  const raw = String(value || '').trim();
  if (!raw) {
    return null;
  }
  const upper = raw.toUpperCase();
  if (upper.length === 2 && REGION_TIMEZONES[upper]) {
    return upper;
  }
  return STATE_NAME_TO_CODE[upper] || null;
};

const stripHtml = value => {
  if (value == null) {
    return '';
  }
  return String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const isSeaticsImageUrl = url => {
  if (!url) {
    return false;
  }
  try {
    const { hostname, pathname } = new URL(String(url));
    const host = hostname.toLowerCase();
    const path = pathname.toLowerCase();
    return (
      host === 'maps.seatics.com' ||
      host.endsWith('.maps.seatics.com') ||
      (host.includes('seatics.com') && /(_tn|_svgc|seat|map)/i.test(path))
    );
  } catch (e) {
    return /maps\.seatics\.com|seatics\.com/i.test(String(url));
  }
};

const isSchemaImageUrlAllowed = url => {
  const raw = typeof url === 'string' ? url : url?.url;
  return !!raw && !isSeaticsImageUrl(raw);
};

const truncateDescription = (text, maxLen = 300) => {
  const trimmed = stripHtml(text);
  if (trimmed.length <= maxLen) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLen - 3).trim()}...`;
};

/**
 * Listing qualifies for Event JSON-LD (TN catalog or native listings with event dates).
 */
export const isEventListing = listing => {
  const pd = listing?.attributes?.publicData || {};
  if (isTicketNetworkEventListing(pd)) {
    return true;
  }
  return !!listingEventCalendarDayFromPublicData(pd);
};

const timezoneFromPublicData = publicData => {
  const pd = publicData || {};
  const explicit = String(pd.timezone || pd.timeZone || pd.venueTimezone || '').trim() || null;
  if (explicit) {
    return explicit;
  }
  const stateCode = normalizeUsStateCode(
    pd.venueState || pd.location?.state || pd.location?.addressRegion
  );
  if (stateCode && REGION_TIMEZONES[stateCode]) {
    return REGION_TIMEZONES[stateCode];
  }
  return null;
};

const WALL_CLOCK_MATCH = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;

/**
 * Format wall-clock date/time in a venue IANA zone (e.g. 13:00 in America/New_York → …-04:00).
 */
const formatWallClockInZone = (raw, timeZone) => {
  const s = String(raw || '').trim();
  const match = s.match(WALL_CLOCK_MATCH);
  if (!match || !timeZone) {
    return null;
  }
  const wall = `${match[1]} ${match[2]}:${match[3]}:${match[4] || '00'}`;
  const m = moment.tz(wall, 'YYYY-MM-DD HH:mm:ss', timeZone);
  if (!m.isValid()) {
    return null;
  }
  return m.format('YYYY-MM-DDTHH:mm:ssZ');
};

const isUtcOrZeroOffset = offset => {
  if (!offset) {
    return false;
  }
  const o = String(offset).toUpperCase();
  return o === 'Z' || o === '+00:00' || o === '-00:00';
};

/**
 * Format a raw event datetime for schema.org:
 * - Keep a real local offset when present (e.g. 2026-08-26T13:00:00-04:00)
 * - Naive / Z / +00:00 showtimes are treated as venue-local wall clock when a zone is known
 * - Date-only → YYYY-MM-DD (no fake midnight UTC)
 * Never use moment.parseZone on naive strings (it invents +00:00).
 */
const formatSchemaStartCandidate = (raw, publicData) => {
  if (raw == null || String(raw).trim() === '') {
    return null;
  }
  const s = String(normalizeListingEventDateString(raw)).trim();
  if (!s || s === '[object Object]') {
    return null;
  }

  if (ISO_DAY.test(s)) {
    return s;
  }

  const tz = timezoneFromPublicData(publicData);
  const offsetMatch = s.match(
    /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(\.\d+)?([zZ]|[+-]\d{2}:\d{2})$/
  );

  if (offsetMatch) {
    const offset = offsetMatch[3] === 'Z' || offsetMatch[3] === 'z' ? 'Z' : offsetMatch[3];
    // TN / parsers often stamp local wall times as Z or +00:00. Re-zone when we know the venue.
    if (tz && isUtcOrZeroOffset(offset)) {
      const zoned = formatWallClockInZone(s, tz);
      if (zoned) {
        return zoned;
      }
    }
    if (!isUtcOrZeroOffset(offset)) {
      return `${offsetMatch[1]}${offset}`;
    }
    // Zero offset and no venue zone — do not emit misleading +00:00; fall through.
  }

  if (ISO_LOCAL_DATETIME.test(s) || eventDateHasExplicitTime(s)) {
    if (tz) {
      const zoned = formatWallClockInZone(s, tz);
      if (zoned) {
        return zoned;
      }
    }
  }

  return null;
};

/**
 * Prefer a timezone-aware local startDate for Google Event markup.
 * Never emit midnight UTC when a local showtime is known.
 * When only a calendar day is known, return YYYY-MM-DD (no time).
 */
export const resolveEventStartDateIso = (publicData, { description } = {}) => {
  const pd = publicData || {};
  const candidates = [
    pd.eventStartDate,
    pd.startDate,
    pd.start_date,
    pd.eventStart,
    pd.event_start,
    eventDateFromListingDescription(description),
    pd.eventDate,
  ];

  // Prefer candidates that include a showtime over date-only values.
  const timed = [];
  const dated = [];
  candidates.forEach(raw => {
    if (raw == null || String(raw).trim() === '') {
      return;
    }
    const s = String(normalizeListingEventDateString(raw)).trim();
    if (eventDateHasExplicitTime(s) || ISO_LOCAL_DATETIME.test(s) || ISO_OFFSET.test(s) || ISO_Z.test(s)) {
      timed.push(raw);
    } else {
      dated.push(raw);
    }
  });

  for (const raw of timed) {
    const formatted = formatSchemaStartCandidate(raw, pd);
    if (formatted) {
      return formatted;
    }
  }
  for (const raw of dated) {
    const formatted = formatSchemaStartCandidate(raw, pd);
    if (formatted) {
      return formatted;
    }
  }

  const day = listingEventCalendarDayFromPublicData(pd);
  return day || null;
};

/**
 * Only return endDate when an explicit ending datetime exists.
 * Do not invent end-of-day / midnight fallbacks (Google discourages this).
 */
export const resolveEventEndDateIso = (publicData, { description } = {}) => {
  const pd = publicData || {};
  const fromDescription = String(description || '').match(/^\s*Event end:\s*([^\r\n]+)/im);
  const candidates = [
    fromDescription?.[1],
    pd.eventEndDate,
    pd.endDate,
    pd.end_date,
    pd.eventEnd,
  ];

  for (const raw of candidates) {
    if (raw == null || String(raw).trim() === '' || /\[object Object\]/i.test(String(raw))) {
      continue;
    }
    const formatted = formatSchemaStartCandidate(raw, pd);
    if (formatted && !ISO_DAY.test(formatted)) {
      return formatted;
    }
  }

  return null;
};

const normalizeOrganizerName = value => stripHtml(value).toLowerCase();

const tryServerLinkableVenuePath = publicData => {
  if (typeof window !== 'undefined') {
    return null;
  }
  try {
    // Server bundle only — omitted from browser build path resolution.
    // eslint-disable-next-line global-require
    const { linkableVenuePathForPublicData } = require('../../server/api-util/linkableVenuePath');
    return linkableVenuePathForPublicData(publicData) || null;
  } catch (e) {
    return null;
  }
};

const absoluteSameSitePath = (marketplaceRootURL, relativePath) => {
  const path = String(relativePath || '').trim();
  const root = String(marketplaceRootURL || '').trim().replace(/\/$/, '');
  if (!path || !root) {
    return null;
  }
  return `${root}${path.startsWith('/') ? path : `/${path}`}`;
};

const listingOfferValidFromIso = listing => {
  const raw = listing?.attributes?.createdAt;
  if (raw == null || String(raw).trim() === '') {
    return null;
  }
  const parsed = Date.parse(String(raw));
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return new Date(parsed).toISOString();
};

export const generateEventDescription = ({ title, venue, calendarDay, marketplaceName }) => {
  const name = stripHtml(title) || 'Event';
  const venuePart = venue ? ` at ${stripHtml(venue)}` : '';
  const datePart = calendarDay ? ` on ${calendarDay}` : '';
  const base = `${name}${venuePart}${datePart}. Browse tickets and availability on ${marketplaceName || 'PromoTix'}.`;
  return truncateDescription(base, 300);
};

const venueNameFromPublicData = publicData => {
  const pd = publicData || {};
  return (
    stripHtml(pd.venueName) ||
    stripHtml(pd.venue) ||
    stripHtml(pd.location?.name) ||
    null
  );
};

const postalAddressFromPublicData = (publicData, geolocation) => {
  const pd = publicData || {};
  const loc = pd.location || {};
  const address = {};

  const street =
    stripHtml(pd.venueStreetAddress) ||
    stripHtml(loc.street) ||
    stripHtml(loc.address);
  const locality = stripHtml(pd.venueCity) || stripHtml(loc.city) || stripHtml(loc.addressLocality);
  const region = stripHtml(pd.venueState) || stripHtml(loc.state) || stripHtml(loc.addressRegion);
  const postalCode = stripHtml(pd.venuePostalCode) || stripHtml(loc.postalCode) || stripHtml(loc.zip);
  const country = stripHtml(pd.venueCountry) || stripHtml(loc.country) || 'US';

  if (street) {
    address.streetAddress = street;
  }
  if (locality) {
    address.addressLocality = locality;
  }
  if (region) {
    address.addressRegion = region;
  }
  if (postalCode) {
    address.postalCode = postalCode;
  }
  if (country) {
    address.addressCountry = country;
  }

  if (Object.keys(address).length === 0 && geolocation?.lat != null && geolocation?.lng != null) {
    return null;
  }

  if (Object.keys(address).length === 0) {
    return null;
  }

  return {
    '@type': 'PostalAddress',
    ...address,
  };
};

const buildEventLocation = ({ publicData, geolocation }) => {
  const name = venueNameFromPublicData(publicData);
  const address = postalAddressFromPublicData(publicData, geolocation);

  if (!name && !address && !geolocation) {
    return null;
  }

  const place = {
    '@type': 'Place',
  };

  if (name) {
    place.name = name;
  }
  if (address) {
    place.address = address;
  }
  if (geolocation?.lat != null && geolocation?.lng != null) {
    place.geo = {
      '@type': 'GeoCoordinates',
      latitude: geolocation.lat,
      longitude: geolocation.lng,
    };
  }

  return place;
};

const organizerFromPublicData = (publicData, { marketplaceRootURL, linkableVenuePath = null } = {}) => {
  const pd = publicData || {};
  const venueName = venueNameFromPublicData(pd);
  const explicitName =
    stripHtml(pd.organizerName) ||
    stripHtml(pd.organizer) ||
    stripHtml(pd.promoter) ||
    stripHtml(pd.promoterName) ||
    null;
  const name = explicitName || venueName;

  if (!name) {
    return null;
  }

  const organizer = {
    '@type': 'Organization',
    name,
  };

  const venuePath = linkableVenuePath ?? tryServerLinkableVenuePath(pd);
  const organizerIsVenue =
    !explicitName ||
    (venueName && normalizeOrganizerName(explicitName) === normalizeOrganizerName(venueName));

  if (organizerIsVenue && venuePath) {
    const url = absoluteSameSitePath(marketplaceRootURL, venuePath);
    if (url) {
      organizer.url = url;
    }
  }

  return organizer;
};

const performerFromPublicData = publicData => {
  const pd = publicData || {};
  const name =
    stripHtml(pd.performerName) ||
    stripHtml(pd.headlinersAndArtists) ||
    stripHtml(pd.eventName);
  if (!name) {
    return null;
  }
  return {
    '@type': 'PerformingGroup',
    name,
  };
};

/**
 * Build performer schema nodes from enrichment / SEO copy when available.
 * Falls back to a single PerformingGroup from publicData.
 */
const performersFromEnrichment = (publicData, enrichmentPerformers, marketplaceRootURL) => {
  const list = Array.isArray(enrichmentPerformers) ? enrichmentPerformers.filter(p => p?.name) : [];
  if (list.length) {
    return list.map(p => {
      const node = {
        '@type': 'PerformingGroup',
        name: p.name,
      };
      if (p.path) {
        const url = absoluteSameSitePath(marketplaceRootURL, p.path);
        if (url) {
          node.url = url;
        }
      }
      return node;
    });
  }
  const single = performerFromPublicData(publicData);
  return single ? [single] : [];
};

const resolveSchemaImages = ({ schemaImageUrls, config }) => {
  const urls = (schemaImageUrls || [])
    .filter(isSchemaImageUrlAllowed)
    .map(img => (typeof img === 'string' ? img : img?.url))
    .filter(Boolean)
    .map(url =>
      url.startsWith('/') && !url.startsWith('//')
        ? absoluteSameSitePath(config?.marketplaceRootURL, url) || url
        : url
    );
  if (urls.length > 0) {
    return urls;
  }
  const fallback = config?.branding?.facebookImage;
  return fallback ? [fallback] : [];
};

const buildEventOffers = ({
  canonicalUrl,
  price,
  currentStock,
  marketplaceName,
  marketplaceRootURL,
  validFrom,
}) => {
  const availability = currentStock > 0 ? IN_STOCK : OUT_OF_STOCK;
  const offer = {
    '@type': 'Offer',
    url: canonicalUrl,
    availability,
    seller: {
      '@type': 'Organization',
      name: marketplaceName,
      url: marketplaceRootURL,
    },
  };

  if (price) {
    offer.price = convertMoneyToNumber(price);
    offer.priceCurrency = price.currency;
  }

  if (validFrom) {
    offer.validFrom = validFrom;
  }

  return offer;
};

/**
 * Build Google Event JSON-LD for a listing.
 * Returns an array when breadcrumb / related entity nodes are present.
 */
export const buildEventSchema = ({
  listing,
  config,
  location,
  routeConfiguration,
  schemaImageUrls,
  intl,
  linkableVenuePath = null,
  seoDescription = null,
  seoCopy = null,
}) => {
  const attributes = listing?.attributes || {};
  const { title = '', description = '', price = null, publicData = {}, geolocation = null } =
    attributes;
  const marketplaceName = config.marketplaceName;
  const marketplaceRootURL = config.marketplaceRootURL;

  const canonicalPath = canonicalRoutePath(routeConfiguration, location, true);
  const canonicalUrl = `${marketplaceRootURL}${canonicalPath}`;

  const calendarDay = listingEventCalendarDayFromPublicData(publicData);
  const venue = venueNameFromPublicData(publicData);
  const assembledSeo =
    seoCopy ||
    buildListingSeoCopy({
      title,
      publicData,
      description,
      marketplaceName,
    });
  const assembledIntro =
    stripHtml(seoDescription) ||
    assembledSeo?.introParagraph ||
    null;
  const eventDescription = truncateDescription(
    assembledIntro ||
      stripHtml(description) ||
      generateEventDescription({ title, venue, calendarDay, marketplaceName }),
    300
  );

  const startDate = resolveEventStartDateIso(publicData, { description });
  const endDate = resolveEventEndDateIso(publicData, { description });
  const images = resolveSchemaImages({ schemaImageUrls, config });
  // Closed listings / past dates are not purchasable — report OutOfStock regardless of
  // the Flex stock relationship so Google shows accurate offer state on those URLs.
  const currentStock = tnListingUnavailableReason(listing)
    ? 0
    : listing?.currentStock?.attributes?.quantity ?? 0;
  const offerValidFrom = listingOfferValidFromIso(listing);
  const eventName = resolveEventEntityName({ title, publicData });
  const venuePath = linkableVenuePath ?? tryServerLinkableVenuePath(publicData);

  const schema = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Event',
    name: eventName,
    description: eventDescription,
    eventStatus: EVENT_SCHEDULED,
    eventAttendanceMode: OFFLINE_ATTENDANCE,
    offers: buildEventOffers({
      canonicalUrl,
      price,
      currentStock,
      marketplaceName,
      marketplaceRootURL,
      validFrom: offerValidFrom,
    }),
  };

  if (startDate) {
    schema.startDate = startDate;
  }
  if (endDate) {
    schema.endDate = endDate;
  }

  const eventLocation = buildEventLocation({ publicData, geolocation });
  if (eventLocation) {
    if (venuePath) {
      const venueUrl = absoluteSameSitePath(marketplaceRootURL, venuePath);
      if (venueUrl) {
        eventLocation.url = venueUrl;
        eventLocation['@type'] = eventLocation['@type'] || 'Place';
      }
    }
    schema.location = eventLocation;
  }

  if (images.length > 0) {
    schema.image = images.length === 1 ? images[0] : images;
  }

  const organizer = organizerFromPublicData(publicData, {
    marketplaceRootURL,
    linkableVenuePath: venuePath,
  });
  if (organizer) {
    schema.organizer = organizer;
  }

  const performers = performersFromEnrichment(
    publicData,
    assembledSeo?.performerLineup,
    marketplaceRootURL
  );
  if (performers.length === 1) {
    schema.performer = performers[0];
  } else if (performers.length > 1) {
    schema.performer = performers;
  }

  // Semantic category relationship (listing ↔ category).
  const categoryLabel =
    assembledSeo?.fields?.snapshotCategory ||
    assembledSeo?.fields?.eventType ||
    stripHtml(publicData.category) ||
    stripHtml(publicData.eventCategory);
  if (categoryLabel) {
    schema.about = {
      '@type': 'Thing',
      name: categoryLabel,
    };
  }

  // City hub relationship when enrichment/seo copy has a path.
  const cityPath = assembledSeo?.links?.cityPath;
  if (cityPath) {
    const cityUrl = absoluteSameSitePath(marketplaceRootURL, cityPath);
    if (cityUrl) {
      schema.isRelatedTo = [
        ...(Array.isArray(schema.isRelatedTo) ? schema.isRelatedTo : []),
        {
          '@type': 'City',
          name:
            assembledSeo?.fields?.cityDisplayLabel ||
            assembledSeo?.fields?.cityLabel ||
            'City',
          url: cityUrl,
        },
      ];
    }
  }

  return schema;
};

/**
 * Legacy Product schema for non-event listings (Sharetribe default behavior).
 */
export const buildProductSchema = ({
  listing,
  config,
  location,
  routeConfiguration,
  schemaImageUrls,
  intl,
  schemaTitle,
}) => {
  const attributes = listing?.attributes || {};
  const { description = '', price = null } = attributes;
  const canonicalPath = canonicalRoutePath(routeConfiguration, location, true);
  const productURL = `${config.marketplaceRootURL}${canonicalPath}`;
  const images = resolveSchemaImages({ schemaImageUrls, config });

  const schemaPriceMaybe = price
    ? {
        price: convertMoneyToNumber(price),
        priceCurrency: price.currency,
      }
    : {};

  const currentStock = listing?.currentStock?.attributes?.quantity ?? 0;
  const schemaAvailability = currentStock > 0 ? IN_STOCK : OUT_OF_STOCK;

  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Product',
    description: stripHtml(description),
    name: schemaTitle,
    image: images,
    offers: {
      '@type': 'Offer',
      url: productURL,
      ...schemaPriceMaybe,
      availability: schemaAvailability,
    },
  };
};

/**
 * Pick Event vs Product schema for a listing page.
 * Event pages may return [Event, BreadcrumbList] when seoCopy breadcrumbs exist.
 */
export const buildListingPageSchema = params => {
  const { listing, config, seoCopy = null } = params;
  if (isEventListing(listing)) {
    const eventSchema = buildEventSchema(params);
    const breadcrumbs = seoCopy?.breadcrumbs || [];
    const breadcrumbSchema = buildBreadcrumbListSchema(
      breadcrumbs,
      config?.marketplaceRootURL
    );
    return breadcrumbSchema ? [eventSchema, breadcrumbSchema] : eventSchema;
  }
  return buildProductSchema(params);
};

/**
 * Non-blocking validation for ops / backfill scripts. Returns { ok, warnings, missing }.
 */
export const validateEventStructuredData = (listing, schemaMaybe) => {
  const warnings = [];
  const missing = [];

  if (!isEventListing(listing)) {
    return { ok: true, warnings: [], missing: [], skipped: true };
  }

  const schema = schemaMaybe || {};
  const required = [
    ['name', schema.name],
    ['startDate', schema.startDate],
    ['description', schema.description],
    ['offers.url', schema.offers?.url],
    ['location.name', schema.location?.name],
    ['image', schema.image],
  ];

  required.forEach(([field, value]) => {
    if (value == null || value === '') {
      missing.push(field);
      warnings.push(`Missing recommended Event field: ${field}`);
    }
  });

  if (!schema.organizer) {
    warnings.push('Missing optional Event field: organizer (no upstream organizerName set)');
  }

  return {
    ok: missing.length === 0,
    warnings,
    missing,
    skipped: false,
  };
};
